import _ from "lodash";
import { useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { getContractApply } from "src/api/contract/contract-api";
import { ContractApplicationModel, ContractManageList, ContractSearchParams } from "src/api/contract/contract-types";
import { ApplicationFormModel } from "src/api/product/product-types";
import { Order, UnionCmdType } from "src/api/public-types";
import { BaseButton, BaseTable } from "src/components";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useUsageApplication, { ProductApplication } from "src/pages/product/hooks/useUsageApplication";
import { useModal } from "src/recoil/modalState/hook";
import { BULLET } from "src/utils/common-util";
import UsageApplicationDetail from "../../contract/form/components/application/component/UsageApplicationDetail";
import UsageApplicationForm from "../../contract/form/components/application/component/UsageApplicationForm";
import { columns } from "../../contract/list/columns/Columns";
import GoToListButton from "src/components/GoToListButton";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import SearchContractPopup from "src/pages/commonPopup/SearchContractPopup";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { getGuideListAsync, getGuideServiceAssociationsAsync, postGuideMappingAsync } from "src/api/guide/guide-api";
import { axiosInstance } from "src/utils";
import { Cell } from "react-table";
import { ServiceUserGuide } from "src/api/guide/guide-types";
import { UnionServiceType } from "src/api/adminMemo/adminmemo-types";
import { useQueryParams } from "src/hooks/useQueryParams";
import ContractManageIdLinkCell from "src/components/contract/ContractManageIdLinkCell";

/**
 * 이용안내 > 계약 목록 화면
 */
const GuideContractList = () => {
  const {
    queryParams: { id },
  } = useQueryParams();
  const { isAuthority } = usePartnerAuthority();
  const { setAbstractModalZ1, setAbstractModalZ2, setBaseModal } = useModal();
  const ServiceType = "SERVICE_CONTRACT_MANAGE";

  const location = useLocation();

  // 모달 오픈
  const [isOpenContractPopup, setIsOpenContractPopup] = useState(false);

  // 이용안내 상세 정보
  const { data: guideDetailData } = useQuery({
    enabled: !!id,
    queryKey: ["getUserGuideDetailAsync", id],
    queryFn: () => getGuideListAsync(axiosInstance, { page: 0, size: 1, idList: id }),
    select: (res) => res?.data?.data,
  });

  // provider Id
  const providerId = useMemo(() => guideDetailData?.content?.[0]?.providerId, [guideDetailData]);

  // 이용안내 제목
  const subject = useMemo(() => guideDetailData?.content?.[0]?.subject || "", [guideDetailData]);

  // 연관 상품 조회
  const { data: guideServiceAssociationsData, refetch: refetchGuideServiceAssociations, isFetched: isFetchedGuideServiceAssociations } = useQuery({
    enabled: !!id,
    queryKey: ["getGuideServiceAssociationsAsync", id, ServiceType],
    queryFn: () => getGuideServiceAssociationsAsync(axiosInstance, { pathVariable: { id: Number(id) }, queryParam: { serviceTypes: ServiceType } }),
    select: (res) => res.data.data.content,
  });

  // 연결정보 저장
  const { mutateAsync: mutateAsyncSaveGuideMapping } = useMutation({
    mutationFn: async (param: ServiceUserGuide[]) => await postGuideMappingAsync(axiosInstance, param),
    onSuccess: () => {
      // 연결정보 재조회
      refetchGuideServiceAssociations();
    },
  });

  const selectedContractManageIds = useMemo(() => {
    return guideServiceAssociationsData?.map((association) => association.serviceId) || [];
  }, [guideServiceAssociationsData]);

  // 삭제 처리 컬럼
  const deleteColumn = useMemo(() => {
    return {
      Header: "삭제",
      accessor: "deleteButton",
      width: 60,
      Cell: ({ row }: Cell<ContractManageList>) => {
        return (
          <button
            className="base-trash-btn color-gray"
            onClick={(e) => {
              e.preventDefault();
              setBaseModal({
                isOpen: true,
                title: "항목을 삭제하시겠습니까?",
                onClick: async () => {
                  console.debug("삭제할 항목 contractManageId", row.original.contractManageId);
                  console.debug("guideServiceAssociationsData", guideServiceAssociationsData);
                  setBaseModal({ isOpen: false });

                  if (!_.isEmpty(guideServiceAssociationsData)) {
                    const userGuideDeleteMappings: ServiceUserGuide[] = (guideServiceAssociationsData || [])
                      .filter((association) => {
                        return String(association.serviceId) === String(row.original.contractManageId);
                      })
                      .map((association) => ({
                        id: association.id,
                        serviceId: association.serviceId,
                        serviceType: association.serviceType as UnionServiceType,
                        cmdType: "D" as UnionCmdType,
                      }));

                    await mutateAsyncSaveGuideMapping(userGuideDeleteMappings);

                    updateSearchFilter({});
                  }
                },
                onClose: () => {
                  setBaseModal({ isOpen: false });
                },
                btnLeftTitle: "취소",
                btnRightTitle: "확인",
              });
            }}
          ></button>
        );
      },
      sticky: "right",
    };
  }, [setBaseModal, guideServiceAssociationsData, id, mutateAsyncSaveGuideMapping]);

  const contractListColumns = useMemo(() => {
    // 신청/계약 목록 컬럼을 그대로 쓰지만, 이용안내는 계약관리 단위 데이터라 계약 ID 대신 계약관리 ID만 표기한다
    const defaultColumns = columns
      .filter((column) => column.accessor !== "contractId")
      .map((column) => {
        const result = _.omit(column, ["sticky"]); // 상품 컬럼 sticky 속성 제거

        // 계약관리 ID 단독 표기 화면이라 대표 계약(계약상태 '이용중' 우선, 없으면 최신) 상세를 새 탭으로 연다
        if (column.accessor === "contractManageId") {
          return {
            ...result,
            Cell: ({ data, row }: any) => (
              <ContractManageIdLinkCell
                contractManageId={row.original.contractManageId}
                contractManageIdsInPage={data.map((item: ContractManageList) => item.contractManageId)}
              />
            ),
          };
        }

        return result;
      });

    const cols = [...defaultColumns];

    if (isAuthority("d")) {
      cols.push(deleteColumn);
    }

    return cols;
  }, [deleteColumn, isAuthority]);

  // URL 쿼리 파라미터 관리
  const { queryParams, queryParamsNavigate } = useQueryParams<ContractSearchParams>({
    page: 0,
    size: 20,
    sort: { orders: [{ property: "id", direction: "DESC" }] },
  });

  // id, tab, list 쿼리 파라미터 제외, productIds 추가
  const filteredQueryParams = useMemo(() => {
    const { id, tab, list, ...rest } = (queryParams as { [key: string]: any }) || {};

    const result = { ...rest };
    const contractManageIdsJoin = selectedContractManageIds?.join(",");
    if (contractManageIdsJoin) {
      result.contractManageIds = contractManageIdsJoin;
    }
    return result;
  }, [queryParams, selectedContractManageIds]);

  // 검색 필터 변경 시 URL 업데이트
  const updateSearchFilter = (updates: Partial<ContractSearchParams>) => {
    queryParamsNavigate({ ...queryParams, ...updates, page: 0 });
  };

  // 계약 목록 조회
  const { data: contractListData, isFetched: isFetchedContractListData } = useQuery({
    enabled: !_.isEmpty(selectedContractManageIds),
    queryKey: ["getContractApply", filteredQueryParams],
    queryFn: () => getContractApply(axiosInstance, filteredQueryParams),
    select: (res) => res.data,
    placeholderData: keepPreviousData,
  });

    // 목록 아이템
  const listData = useMemo(() => {
    if (selectedContractManageIds.length === 0) {
      return [];
    }

    return contractListData?.data?.content || [];
  }, [contractListData, selectedContractManageIds]);

  // Total Pages
  const totalPages = useMemo(() => {
    if (selectedContractManageIds.length === 0) {
      return 0;
    }

    return contractListData?.meta?.pageMeta?.totalPages || 0;
  }, [contractListData, selectedContractManageIds]);

  // Total Elements
  const totalElements = useMemo(() => {
    if (selectedContractManageIds.length === 0) {
      return 0;
    }

    return contractListData?.meta?.pageMeta?.totalElements || 0;
  }, [contractListData, selectedContractManageIds]);

  const { getApplicationSummury, getContractUsageApplication, onUpdateContractApplication } = useUsageApplication();

  const [contractApplication, setContractApplication] = useState<ContractApplicationModel | null>();

  // 이용신청서 모달 오픈
  const getContractApplication = async (passContractId: number) => {
    const result = await getContractUsageApplication({ contractId: Number(passContractId) });
    setContractApplication(result);
  };

  // 계약 신청서 수정
  const onUpdateCtApplication = (formData: ApplicationFormModel, contractId: number) => {
    setBaseModal({
      isOpen: true,
      title: "저장하시겠습니까?",
      onClick: () => {
        execute();
      },
      onClose: () => {
        setBaseModal({ isOpen: false });
      },
      btnRightTitle: "확인",
      btnLeftTitle: "취소",
    });

    const execute = async () => {
      const result = await onUpdateContractApplication({ contractId: Number(contractId), formData: formData });
      if (result) {
        await getContractApplication(contractId);
        setAbstractModalZ1({ isOpen: false });
        setAbstractModalZ2({ isOpen: false });
        setBaseModal({ isOpen: false });
      }
    };
  };

  const openApplicationModal = async (contractId: number) => {
    const contractApplication = await getContractUsageApplication({ contractId: contractId });
    const applicationSummary = await getApplicationSummury({
      quotationNo: "", // 데이터가 없음
      contractApplication: contractApplication!,
      contractId,
    });

    // 수정 form 오픈
    const openApplicationEditModal = () => {
      const formData: ProductApplication = {
        formData: {
          richText: contractApplication?.applicationBody?.formData?.richText || "",
          attachmentConfig: {
            isRequired: contractApplication?.applicationBody?.formData?.attachmentConfig?.isRequired || false,
          },
        },
      };

      setAbstractModalZ2({
        isOpen: true,
        children: (
          <UsageApplicationForm
            fileList={contractApplication?.applicationBody?.attachments || []}
            onClose={() => setAbstractModalZ2({ isOpen: false })}
            applicationSummary={applicationSummary}
            applicationFormData={formData}
            onSaveFormData={(data: ApplicationFormModel) => onUpdateCtApplication(data, contractId)}
          />
        ),
      });
    };

    setAbstractModalZ1({
      isOpen: true,
      children: (
        <UsageApplicationDetail //
          applicationValue={contractApplication?.applicationBody?.formData?.richText || ""}
          onClose={() => setAbstractModalZ1({ isOpen: false })}
          onEdit={openApplicationEditModal}
          applicationSummary={applicationSummary}
          fileList={contractApplication?.applicationBody?.attachments || []}
        />
      ),
    });
  };

  // 테이블 노출 여부
  const visibleTable = useMemo(() => {
    if (isFetchedGuideServiceAssociations && selectedContractManageIds.length === 0) {
      return true;
    }

    if (isFetchedContractListData) {
      return true;
    }

    return false;
  }, [isFetchedGuideServiceAssociations, selectedContractManageIds, isFetchedContractListData]);

  return (
    <div className="contents-container__table justify-contents-between">
      <div className="contents-container__table-with-title-and-btn-wrap">
        <div className="contents-container__search-wrap pt30">
          <div className="left-area">
            <BaseSectionTitle title={"연결정보(신청/계약)"} className="pb0" />
          </div>
          {isAuthority("w") && (
            <div className="right-area">
              <BaseButton
                title="연결 관리"
                onClick={() => {
                  setIsOpenContractPopup(true);
                }}
              />

              {isOpenContractPopup && (
                <SearchContractPopup
                  title={"연결정보(신청/계약)"}
                  description={`${BULLET} 설정중인 이용안내 항목명 : ${subject}`}
                  existContractManageIds={selectedContractManageIds}
                  isContractManageIdColumn
                  onClick={(items) => {
                    console.debug("선택된 계약 목록", items);
                    setIsOpenContractPopup(false);

                    const userGuideMapping: ServiceUserGuide[] = items.map((contract) => ({
                      cmdType: "C",
                      userGuideId: String(id),
                      serviceId: String(contract.contractManageId),
                      serviceType: ServiceType,
                      buildingId: String(contract.buildingId),
                      orderNums: 0,
                      isDisplayed: true,
                    }));

                    mutateAsyncSaveGuideMapping(userGuideMapping);
                  }}
                  onClosed={() => setIsOpenContractPopup(false)}
                  providerIds={providerId}
                />
              )}
            </div>
          )}
        </div>

        {visibleTable && (
          <BaseTable
            data={listData}
            columns={contractListColumns}
            pageIndex={Number(queryParams?.page) || 0}
            currentSize={Number(queryParams.size) || 20}
            sizeOption={(size) => updateSearchFilter({ size })}
            totalPages={totalPages}
            totalElements={totalElements}
            goPage={(page: number) => queryParamsNavigate({ ...queryParams, page })}
            disabledSortHeaders={["mbOrganizationName", "useNums", "approvalType", "approvalStatus", "deleteButton"]}
            orders={queryParams?.sort?.orders}
            setOrders={(orders: Order[]) => orders && updateSearchFilter({ sort: { orders } })}
            openApplicationModal={openApplicationModal}
          />
        )}
      </div>

      <div className="contents-container__btn-wrap">
        <div className="left-area d-flex">
          <GoToListButton />
        </div>
      </div>
    </div>
  );
};

export default GuideContractList;
